const { useState, useEffect } = wp.element;
const { Button, Spinner, Icon } = wp.components;
const { __ } = wp.i18n;
const apiFetch = wp.apiFetch;

/**
 * Giveaway Preview Component
 * Shows the rendered giveaway preview
 */
const GiveawayPreview = ({ giveawayId }) => {
  const [previewHTML, setPreviewHTML] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState('desktop'); // desktop, tablet, mobile

  useEffect(() => {
    fetchPreview();
  }, [giveawayId]);

  const fetchPreview = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch({
        path: `/giveasap/v1/preview/${giveawayId}`,
        method: 'GET',
      });

      if (response && response.html) {
        setPreviewHTML(response.html);
      } else {
        setError(__('No preview available.', 'giveasap'));
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError(__('Failed to load preview. Please try again.', 'giveasap'));
    } finally {
      setIsLoading(false);
    }
  };

  const getDeviceClass = () => {
    switch (device) {
      case 'mobile':
        return 'giveasap-preview-mobile';
      case 'tablet':
        return 'giveasap-preview-tablet';
      default:
        return 'giveasap-preview-desktop';
    }
  };

  return (
    <div className="giveasap-preview-section">
      <div className="giveasap-preview-toolbar">
        <div className="giveasap-preview-devices">
          <Button
            isPressed={device === 'desktop'}
            onClick={() => setDevice('desktop')}
            icon={<Icon icon="desktop" />}
            label={__('Desktop View', 'giveasap')}
            className="giveasap-device-button"
          />
          <Button
            isPressed={device === 'tablet'}
            onClick={() => setDevice('tablet')}
            icon={<Icon icon="tablet" />}
            label={__('Tablet View', 'giveasap')}
            className="giveasap-device-button"
          />
          <Button
            isPressed={device === 'mobile'}
            onClick={() => setDevice('mobile')}
            icon={<Icon icon="smartphone" />}
            label={__('Mobile View', 'giveasap')}
            className="giveasap-device-button"
          />
        </div>
        <Button
          onClick={fetchPreview}
          icon="update"
          label={__('Refresh Preview', 'giveasap')}
          isBusy={isLoading}
          disabled={isLoading}
        >
          {__('Refresh', 'giveasap')}
        </Button>
      </div>

      <div className={`giveasap-preview-container ${getDeviceClass()}`}>
        {isLoading && (
          <div className="giveasap-preview-loading">
            <Spinner />
            <p>{__('Loading preview...', 'giveasap')}</p>
          </div>
        )}

        {error && (
          <div className="giveasap-preview-error">
            <p>{error}</p>
            <Button onClick={fetchPreview} variant="primary">
              {__('Try Again', 'giveasap')}
            </Button>
          </div>
        )}

        {!isLoading && !error && previewHTML && (
          <div
            className="giveasap-preview-content"
            dangerouslySetInnerHTML={{ __html: previewHTML }}
          />
        )}
      </div>
    </div>
  );
};

export default GiveawayPreview;
